import { makeAutoObservable, runInAction } from "mobx";
import type { CmsItem, PlayableSummaryBatch } from "@hive/sdk";
import { hive } from "../lib/apiClient";
import {
	assembleExploreData,
	assembleExploreSection,
	exploreIds,
	exploreSections,
	type ExploreData,
} from "../lib/exploreContent";

function dictionaries(batch: PlayableSummaryBatch) {
	return {
		playables: Object.fromEntries(batch.playables.map((item) => [item.id, item])),
		authors: batch.users,
	};
}

export class ExploreStore {
	data: ExploreData | null = null;
	loading = false;
	error = false;
	collectionsLoading = new Set<string>();
	collectionsFailed = new Set<string>();
	private collectionsLoaded = new Set<string>();
	private context = "";
	private generation = 0;
	historyVersion = 0;
	private viewer: string | null = null;
	private historyRequested = "";
	private historyLoaded = "";

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	seed(data: ExploreData, locale: string, viewer: string | null) {
		this.generation++;
		this.context = `${locale}:${viewer ?? "guest"}`;
		this.viewer = viewer;
		this.data = data;
		this.error = this.loading = false;
		this.historyRequested = this.historyLoaded = "";
		this.collectionsLoaded.clear();
		this.collectionsLoading.clear();
		this.collectionsFailed.clear();
	}

	async ensureLoaded(locale: string, viewer: string | null, force = false) {
		const context = `${locale}:${viewer ?? "guest"}`;
		if (!force && this.context === context && (this.data || this.loading)) {
			if (this.data && viewer) void this.loadHistory();
			return;
		}
		if (this.context !== context) this.data = null;
		else if (this.data) this.data.sections = this.data.sections.map((section) => section.playHistory ? { ...section, items: [] } : section);
		this.context = context;
		this.viewer = viewer;
		const generation = ++this.generation;
		this.loading = true;
		this.error = false;
		this.historyRequested = this.historyLoaded = "";
		this.collectionsLoaded.clear();
		this.collectionsLoading.clear();
		this.collectionsFailed.clear();
		try {
			const manifest = await hive.pages.get("app-explore", { view: "manifest", locale });
			const ids = exploreIds(manifest);
			const batch = ids.length ? await hive.playables.getManySummaries(ids) : { playables: [], users: {}, unavailableIds: [] };
			if (generation !== this.generation) return;
			const { playables, authors } = dictionaries(batch);
			runInAction(() => {
				this.data = assembleExploreData(manifest, playables, authors);
			});
			if (viewer) void this.loadHistory();
		} catch {
			if (generation === this.generation)
				runInAction(() => {
					this.error = true;
				});
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.loading = false;
				});
		}
	}

	async loadCollection(id: string) {
		if (!this.data || this.collectionsLoaded.has(id) || this.collectionsLoading.has(id)) return;
		const section = exploreSections(this.data.manifest).find((entry) => entry.id === id);
		if (!section || section.playHistory) return;
		const generation = this.generation;
		this.collectionsLoading.add(id);
		this.collectionsFailed.delete(id);
		try {
			const ids = exploreIds({ ...this.data.manifest, sections: [section] }, Infinity);
			const batch = ids.length ? await hive.playables.getManySummaries(ids) : { playables: [], users: {}, unavailableIds: [] };
			if (generation !== this.generation || !this.data) return;
			const { playables, authors } = dictionaries(batch);
			runInAction(() => {
				this.data!.sections = this.data!.sections.map((entry) =>
					entry.id === id ? assembleExploreSection(section, playables, authors, Infinity) : entry,
				);
				this.collectionsLoaded.add(id);
			});
		} catch {
			if (generation === this.generation)
				runInAction(() => {
					this.collectionsFailed.add(id);
				});
		} finally {
			if (generation === this.generation)
				runInAction(() => {
					this.collectionsLoading.delete(id);
				});
		}
	}

	isCollectionLoaded(id: string) { return this.collectionsLoaded.has(id); }
	isViewer(viewer: string | null) { return this.viewer === viewer; }

	/** Like App, re-read after a play report settles; a plain return to Explore reuses the rows. */
	markPlayed(viewer: string) {
		if (this.viewer === viewer) this.historyVersion++;
	}

	/** Each CMS instruction is independent. Never substitute browse history or a default kind. */
	private async loadHistory() {
		if (!this.data || !this.viewer) return;
		const generation = this.generation;
		const version = this.historyVersion;
		const sections = this.data.sections.filter((section) => section.playHistory);
		const stamp = JSON.stringify([generation, version, sections.map(({ id, playHistory }) => [id, playHistory])]);
		if (stamp === this.historyRequested || stamp === this.historyLoaded) return;
		this.historyRequested = stamp;
		this.data.sections = this.data.sections.map((section) => section.playHistory ? { ...section, items: [] } : section);
		const results = await Promise.all(sections.map(async (section): Promise<[string, CmsItem[]]> => {
			try {
				const page = await hive.history.listPlayed(section.playHistory!);
				return [section.id, page.items
					.filter(({ playable }) => !playable.kind || ["game", "drama"].includes(playable.kind))
					.map(({ playable }) => ({ type: "playable", playable, title: null, imageUrl: null, linkUrl: null }))];
			} catch {
				// App hides a failed/empty history section; other configured sections still render.
				return [section.id, []];
			}
		}));
		if (generation !== this.generation || version !== this.historyVersion || !this.data) return;
		const items = new Map(results);
		runInAction(() => {
			this.data!.sections = this.data!.sections.map((section) =>
				section.playHistory ? { ...section, items: items.get(section.id) ?? [] } : section);
			this.historyLoaded = stamp;
		});
	}
}
